import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Check, EyeOff, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  approved: { bg: "var(--mocha-brown)", fg: "#FBF6F3", label: "Live on site" },
  pending: { bg: "#F3DAD5", fg: "var(--rose-mocha-deep)", label: "Awaiting approval" },
  hidden: { bg: "#EEE7E2", fg: "var(--text-secondary)", label: "Hidden" },
};

export default function ReviewsPanel({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [reviews, setReviews] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ name: "", email: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([axios.get(`${API}/admin/reviews`, h), axios.get(`${API}/admin/review-requests`, h)])
      .then(([r, q]) => {
        setReviews(r.data);
        setRequests(q.data);
      })
      .catch(() => toast.error("Could not load reviews."));
  }, [token]);

  const request = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Add the client's name and email.");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/review-request`, form, auth);
      toast.success(`Review request sent to ${form.email}`);
      setRequests((rs) => [{ id: data.id, ...form, submitted: false, created_at: new Date().toISOString() }, ...rs]);
      setForm({ name: "", email: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send the review request.");
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}`, { status }, auth);
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(status === "approved" ? "Review is now live on your site." : "Review hidden.");
    } catch {
      toast.error("Could not update the review.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, auth);
      setReviews((rs) => rs.filter((r) => r.id !== id));
      toast.success("Review deleted.");
    } catch {
      toast.error("Could not delete the review.");
    }
  };

  return (
    <div data-testid="admin-reviews-panel" className="flex flex-col gap-6">
      <RequestForm form={form} setForm={setForm} sending={sending} onSubmit={request} />
      <SentRequests requests={requests} />
      {reviews.length === 0 && <p style={{ color: "var(--text-secondary)" }}>No reviews submitted yet.</p>}
      {reviews.map((r) => (
        <ReviewCard key={r.id} r={r} onStatus={setStatus} onRemove={remove} />
      ))}
    </div>
  );
}

function RequestForm({ form, setForm, sending, onSubmit }) {
  const input = "flex-1 min-w-0 bg-transparent py-2 outline-none text-sm";
  const line = { borderBottom: "1px solid var(--border-light)" };
  return (
    <form
      onSubmit={onSubmit}
      data-testid="review-request-form"
      className="rounded-2xl p-5 flex flex-col md:flex-row gap-4 md:items-end"
      style={{ backgroundColor: "#fff", border: "1px solid var(--border-light)" }}
    >
      <p className="text-sm font-medium md:w-44" style={{ color: "var(--text-primary)" }}>Ask a client for a review</p>
      <input data-testid="review-request-name" placeholder="Client name" value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} style={line} />
      <input data-testid="review-request-email" type="email" placeholder="Client email" value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} style={line} />
      <button data-testid="review-request-send" disabled={sending}
        className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm disabled:opacity-60"
        style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}>
        <Send size={14} /> {sending ? "Sending…" : "Send request"}
      </button>
    </form>
  );
}

function SentRequests({ requests }) {
  if (requests.length === 0) return null;
  return (
    <div data-testid="admin-review-requests" className="rounded-2xl p-5 text-sm"
      style={{ backgroundColor: "#fff", border: "1px solid var(--border-light)", color: "var(--text-secondary)" }}>
      <p className="font-medium mb-2" style={{ color: "var(--text-primary)" }}>Requests sent</p>
      <ul className="flex flex-col gap-1.5">
        {requests.map((q) => (
          <li key={q.id} data-testid={`admin-review-request-${q.id}`} className="flex flex-wrap justify-between gap-2">
            <span>{q.name} · {q.email}</span>
            <span style={{ color: q.submitted ? "var(--mocha-brown)" : "var(--rose-mocha-deep)" }}>
              {q.submitted ? "Review received" : `Sent ${new Date(q.created_at).toLocaleDateString("en-US")}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReviewCard({ r, onStatus, onRemove }) {
  const s = STATUS_STYLE[r.status];
  const btn = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs";
  return (
    <div data-testid={`admin-review-${r.id}`} className="rounded-2xl p-5"
      style={{ backgroundColor: "#fff", border: "1px solid var(--border-light)" }}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="font-medium" style={{ color: "var(--text-primary)" }}>{r.name}</span>
        <span className="text-sm" style={{ color: "var(--text-secondary)" }}>· {r.role}</span>
        <span data-testid={`admin-review-status-${r.id}`} className="text-xs px-2.5 py-1 rounded-full"
          style={{ backgroundColor: s.bg, color: s.fg }}>{s.label}</span>
      </div>
      <p className="italic text-sm mb-4" style={{ color: "var(--text-secondary)" }}>"{r.quote}"</p>
      <div className="flex flex-wrap gap-2">
        {r.status !== "approved" && (
          <button data-testid={`admin-review-approve-${r.id}`} onClick={() => onStatus(r.id, "approved")}
            className={btn} style={{ backgroundColor: "var(--accent-gold)", color: "#2E241D" }}>
            <Check size={13} /> Approve
          </button>
        )}
        {r.status === "approved" && (
          <button data-testid={`admin-review-hide-${r.id}`} onClick={() => onStatus(r.id, "hidden")}
            className={btn} style={{ border: "1px solid var(--border-light)", color: "var(--text-secondary)" }}>
            <EyeOff size={13} /> Hide
          </button>
        )}
        <button data-testid={`admin-review-delete-${r.id}`} onClick={() => onRemove(r.id)}
          className={btn} style={{ border: "1px solid var(--border-light)", color: "var(--text-secondary)" }}>
          <Trash2 size={13} /> Delete
        </button>
      </div>
    </div>
  );
}
